import { useState, useEffect } from 'react';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <h1 className="flex items-center gap-2.5 cursor-pointer" onClick={() => scrollTo('hero')}>
          {/* PureCare hands + water logo */}
          <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
            <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Circle background */}
              <circle cx="50" cy="50" r="48" fill="url(#navGrad)" />
              <defs>
                <linearGradient id="navGrad" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
                <linearGradient id="navWater" x1="50" y1="30" x2="50" y2="70" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#e0f7ff" />
                  <stop offset="100%" stopColor="#7dd3fc" />
                </linearGradient>
              </defs>
              {/* Left hand — cupped upward */}
              <path
                d="M14 68 C14 68 12 58 16 52 C18 48 22 47 25 49 L27 44 C29 40 33 39 36 42 L37 39 C39 35 43 35 45 39 L46 42 C47 38 51 37 53 41 L54 38 C56 34 61 35 62 40 L64 48 C66 44 70 44 71 50 L72 60 C73 66 70 72 65 74 L40 74 C28 74 15 72 14 68 Z"
                fill="white"
                fillOpacity="0.95"
              />
              {/* Right hand mirrored — second pair of fingers */}
              <path
                d="M86 68 C86 68 88 58 84 52 C82 48 78 47 75 49 L73 44 C71 40 67 39 64 42"
                stroke="white"
                strokeWidth="0"
                fill="none"
              />
              {/* Water drop / stream on top of hands */}
              <path
                d="M50 22 C50 22 44 32 44 40 C44 46 46.5 50 50 50 C53.5 50 56 46 56 40 C56 32 50 22 50 22 Z"
                fill="url(#navWater)"
                opacity="0.9"
              />
              {/* water ripple lines */}
              <path d="M42 58 Q50 54 58 58" stroke="#bae6fd" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.7"/>
              <path d="M39 63 Q50 58 61 63" stroke="#bae6fd" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5"/>
            </svg>
          </div>
          {/* Wordmark */}
          <div className="flex flex-col leading-none">
            <span className="font-bold text-sm tracking-tight text-gray-900">Pure<span className="text-sky-500">Care</span></span>
            <span className="text-gray-400 text-[9px] tracking-widest uppercase font-medium">Assisted Living</span>
          </div>
        </h1>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-6">
          {/* Phone */}
          <a
            href="tel:6127351448"
            className="flex items-center gap-1.5 text-sm whitespace-nowrap text-gray-600 hover:text-sky-600 transition-colors"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-xs"></i>
            </div>
            (612) 735-1448
          </a>
          {['hero', 'services', 'contact'].map((id) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-sm capitalize cursor-pointer transition-colors whitespace-nowrap text-gray-600 hover:text-gray-900"
            >
              {id === 'hero' ? 'Home' : id}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="text-sm px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get in Touch
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-gray-800`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {['hero', 'services', 'contact'].map((id) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-sm capitalize text-left text-gray-700 hover:text-sky-600 cursor-pointer whitespace-nowrap"
            >
              {id === 'hero' ? 'Home' : id}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="text-sm px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-400 transition-colors w-fit cursor-pointer whitespace-nowrap"
          >
            Get in Touch
          </button>
        </div>
      )}
    </nav>
  );
}
